import { CameraControls, CameraControlsImpl } from '@react-three/drei';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Vector3 } from 'three';
import { useShallow } from 'zustand/shallow';

import { CameraProjectionType } from 'app/modules/wallforming/components/scenes/camera-controls/types/cameraProjectionType';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { EPSILON } from 'shared/constants';

const cameraPosition = new Vector3();
const cameraForward = new Vector3();
const frustumCenter = new Vector3();
const viewerPosition = new Vector3();
const up = new Vector3(0, 0, 1);
const side = new Vector3();
const worldRight = new Vector3(1, 0, 0);

/**
 * FrustumCameraControls — Camera controls for the frustum viewer tab.
 *
 * Provides free-orbit, zoom, and pan controls. On first camera update from the main scene,
 * automatically positions the viewer camera to frame the main camera's frustum for immediate context.
 * Includes imperative method to focus the building geometry.
 */
export const FrustumCameraControls = forwardRef(function FrustumCameraControls(_, ref) {
    const cameraControlsRef = useRef<CameraControls | null>(null);
    const hasAlignedRef = useRef<boolean>(false);

    const { cameraHelperData, buildingHelperRef } = usePlacementStore(
        useShallow((state) => ({
            cameraHelperData: state.cameraHelperData,
            buildingHelperRef: state.buildingHelperRef
        }))
    );

    // Align viewer camera to show the main frustum as soon as the first data arrives.
    useEffect(() => {
        if (!cameraHelperData || hasAlignedRef.current || !cameraControlsRef.current) {
            return;
        }
        hasAlignedRef.current = true;

        const matrixWorld = cameraHelperData.matrixWorld;
        cameraPosition.set(matrixWorld[12], matrixWorld[13], matrixWorld[14]);
        cameraForward.set(-matrixWorld[8], -matrixWorld[9], -matrixWorld[10]).normalize();

        const midDepth = (cameraHelperData.near + cameraHelperData.far) / 2;
        frustumCenter.copy(cameraPosition).addScaledVector(cameraForward, midDepth);

        const nearHeight = cameraHelperData.top - cameraHelperData.bottom;
        const nearWidth = cameraHelperData.right - cameraHelperData.left;

        const frustumSpan =
            cameraHelperData.projectionType === CameraProjectionType.PERSPECTIVE
                ? Math.max(
                      nearHeight * (cameraHelperData.far / Math.max(cameraHelperData.near, EPSILON)),
                      nearWidth * (cameraHelperData.far / Math.max(cameraHelperData.near, EPSILON)),
                      cameraHelperData.far - cameraHelperData.near
                  )
                : Math.max(nearHeight, nearWidth, cameraHelperData.far - cameraHelperData.near);

        const viewDist = frustumSpan * 2.5;
        side.crossVectors(cameraForward, up).normalize();
        if (side.lengthSq() < EPSILON) {
            side.crossVectors(cameraForward, worldRight).normalize();
        }
        viewerPosition
            .copy(frustumCenter)
            .addScaledVector(up, viewDist * 0.6)
            .addScaledVector(side, viewDist * 0.4)
            .addScaledVector(cameraForward.clone().negate(), viewDist * 0.15);

        if (viewerPosition.distanceTo(frustumCenter) < 0.01) {
            viewerPosition.copy(frustumCenter).add(new Vector3(0, viewDist, viewDist));
        }

        cameraControlsRef.current.setLookAt(
            viewerPosition.x,
            viewerPosition.y,
            viewerPosition.z,
            frustumCenter.x,
            frustumCenter.y,
            frustumCenter.z,
            false
        );
    }, [cameraHelperData]);

    useImperativeHandle(
        ref,
        () => ({
            frameBuilding: () => {
                // Focus building
                if (buildingHelperRef.current && cameraControlsRef.current) {
                    buildingHelperRef.current.geometry.computeBoundingSphere();
                    const boundingSphere = buildingHelperRef.current.geometry.boundingSphere;
                    if (!boundingSphere) {
                        return;
                    }
                    const center = boundingSphere.center;
                    cameraControlsRef.current.setTarget(center.x, center.y, center.z, true);
                    cameraControlsRef.current.fitToSphere(boundingSphere, true);
                    cameraControlsRef.current.enabled = true;
                }
            }
        }),
        [buildingHelperRef]
    );

    const handleCameraControlsRef = (controls: CameraControls | null) => {
        cameraControlsRef.current = controls;
    };

    return (
        <CameraControls
            ref={handleCameraControlsRef}
            dollySpeed={1.5}
            draggingSmoothTime={0.07}
            mouseButtons={{
                left: CameraControlsImpl.ACTION.ROTATE,
                middle: CameraControlsImpl.ACTION.TRUCK,
                right: CameraControlsImpl.ACTION.TRUCK,
                wheel: CameraControlsImpl.ACTION.ZOOM
            }}
            infinityDolly
            makeDefault
        />
    );
});
